"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { GravityPicker } from "@/components/tools/physics-mechanics/gravity-picker";
import { InclineDiagram } from "@/components/tools/physics-mechanics/incline-diagram";
import { inclineSteps } from "@/components/tools/physics-mechanics/incline-steps";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  formatSig,
  SIG_FIG_CHOICES,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  DEFAULT_GRAVITY,
  type GravityState,
  readGravity,
} from "@/lib/tool-kit/physics-mechanics/gravity";
import {
  type InclineInput,
  type InclineResult,
  solveIncline,
} from "@/lib/tool-kit/physics-mechanics/incline";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  ANGLE,
  COEFFICIENT,
  FORCE,
  fromBase,
  LENGTH,
  MASS,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

/** English strings. A translated content file overrides these through `ui`. */
export const INCLINED_PLANE_CALCULATOR_STRINGS = {
  instructions:
    "Describe the block and the ramp. The block starts at rest and the ramp is straight and rigid. The block slides if the pull along the slope beats static friction.",
  mass: "Mass of the block m",
  angle: "Angle of the incline θ",
  angleHint: "Between 0° and just under 90°, measured from the horizontal.",
  staticMu: "Coefficient of static friction μs",
  kineticMu: "Coefficient of kinetic friction μk",
  muHint: "Use 0 for a frictionless ramp. μk should not exceed μs.",
  applied: "Push or pull along the slope (optional)",
  appliedHint:
    "Positive pushes up the slope, negative pulls down it. Leave 0 for none.",
  length: "Distance along the slope (optional)",
  lengthHint:
    "Gives the time and speed after sliding that far, and the height of the ramp.",
  gravity: "Gravity",
  gravityEarth: "Earth (9.80665 m/s², standard gravity)",
  gravityMoon: "Moon (1.62 m/s²)",
  gravityMars: "Mars (3.73 m/s², mean)",
  gravityCustom: "Another value",
  gravityCustomLabel: "Gravitational acceleration g",
  unitFor: "Unit for {name}",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  verdictRest: "The block stays at rest",
  verdictDown: "The block slides down the slope",
  verdictUp: "The block moves up the slope",
  verdictLabel: "What happens",
  accelerationDown: "Acceleration, down the slope",
  accelerationUp: "Acceleration, up the slope",
  accelerationRest: "Acceleration",
  frictionActing: "Friction force acting",
  frictionStatic: "Static friction needed to hold it",
  weight: "Weight W = mg",
  parallel: "Component along the slope, W∥",
  perpendicular: "Component into the slope, W⊥",
  normal: "Normal force N",
  maxStatic: "Maximum static friction, μs N",
  kineticFriction: "Kinetic friction, μk N",
  criticalAngle: "Angle at which a free block starts to slide",
  pushHeading: "Pushing the block up the ramp",
  pushToStart: "Push to start it moving up",
  pushToKeep: "Push to keep it moving up at constant speed",
  pushToHold: "Push to stop it sliding down",
  travelHeading: "Over the distance you entered",
  travelTime: "Time to travel the distance",
  travelSpeed: "Speed after that distance",
  rise: "Height of the ramp over that distance",
  advantage: "Ideal mechanical advantage, 1 ÷ sin θ",
  copy: "Copy results",
  copied: "Copied!",
  copyText: "{verdict}; acceleration {a}; normal force {n}; friction {f}",
  workingHeading: "The working",
  stepInputs:
    "In SI units and degrees: m = {m} kg, θ = {angle}°, μs = {mus}, μk = {muk}, g = {g} m/s².",
  stepWeight: "Weight: W = mg = {m} × {g} = {w} N.",
  stepComponents:
    "Split the weight: W∥ = W sin θ = {w} × sin {angle}° = {par} N down the slope, and W⊥ = W cos θ = {w} × cos {angle}° = {perp} N into it.",
  stepNormal:
    "The plane pushes back with a normal force equal to W⊥, so N = {n} N (nothing else presses on or pulls away from the surface).",
  stepMaxStatic: "Largest static friction: μs N = {mus} × {n} = {fmax} N.",
  stepPullFree:
    "With no push or pull, the only force along the slope is gravity's component, {par} N down the slope.",
  stepPullApplied:
    "Along the slope (up is positive): F − W∥ = {applied} − {par} = {pull} N before friction.",
  stepHolds:
    "The pull of {pull} N does not exceed the largest static friction, {fmax} N, so static friction rises to match it and the block stays at rest: a = 0.",
  stepSlides:
    "The pull of {pull} N exceeds the largest static friction, {fmax} N, so the block slides {direction}. Kinetic friction is μk N = {muk} × {n} = {fk} N, opposing the motion.",
  stepNet:
    "Net force along the slope = {net} N, so a = net ÷ m = {net} ÷ {m} = {a} m/s².",
  stepTravel:
    "From rest over {length} m: t = √(2d ÷ a) = √(2 × {length} ÷ {a}) = {time} s, and v = √(2ad) = {speed} m/s.",
  directionUp: "up the slope",
  directionDown: "down the slope",
  diagramHeading: "Forces on the block",
  diagramTitle:
    "A block on an inclined plane with its weight, the normal force, friction and any applied force drawn as arrows",
  diagramWeight: "W",
  diagramNormal: "N",
  diagramFriction: "f",
  diagramApplied: "F",
  diagramNote:
    "Arrow lengths are drawn to one scale relative to the weight. W points straight down, N is perpendicular to the surface, and f and F lie along it.",
  assumptions:
    "Assumes the block starts at rest, the plane is rigid and straight, there is no air resistance, and any applied force is parallel to the slope. Static and kinetic coefficients are approximate and depend on the surfaces.",
  enterPrompt: "Enter the mass and the angle to see the forces.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign.",
  errors: {
    mass: "Mass must be greater than zero.",
    angle: "The angle must be at least 0° and less than 90°.",
    "static-mu": "The coefficient of static friction cannot be negative.",
    "kinetic-mu": "The coefficient of kinetic friction cannot be negative.",
    "kinetic-exceeds-static":
      "The kinetic coefficient cannot be larger than the static one: surfaces grip more before they slip than while they slide.",
    gravity: "g must be greater than zero.",
    length: "The distance along the slope cannot be negative.",
  },
} as const;

type Strings = typeof INCLINED_PLANE_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

interface Field {
  value: string;
  unit: string;
}

interface FormState {
  mass: Field;
  angle: Field;
  staticMu: string;
  kineticMu: string;
  applied: Field;
  length: Field;
  gravity: GravityState;
}

const INITIAL: FormState = {
  mass: { value: "10", unit: "kg" },
  angle: { value: "30", unit: "deg" },
  staticMu: "0.4",
  kineticMu: "0.3",
  applied: { value: "0", unit: "N" },
  length: { value: "2", unit: "m" },
  gravity: DEFAULT_GRAVITY,
};

type Unreadable =
  | "mass"
  | "angle"
  | "staticMu"
  | "kineticMu"
  | "applied"
  | "length"
  | "gravityCustomLabel";

interface Reading {
  input: InclineInput | null;
  unreadable: Unreadable | null;
  missing: boolean;
}

function readForm(form: FormState): Reading {
  const mass = parseQuantity(form.mass.value);
  const angle = parseQuantity(form.angle.value);
  const staticMu = parseQuantity(form.staticMu);
  const kineticMu = parseQuantity(form.kineticMu);
  const applied =
    form.applied.value.trim() === "" ? 0 : parseQuantity(form.applied.value);
  const length =
    form.length.value.trim() === "" ? null : parseQuantity(form.length.value);
  const gravity = readGravity(form.gravity);
  const fail = (unreadable: Unreadable): Reading => ({
    input: null,
    unreadable,
    missing: false,
  });
  if (
    [form.mass.value, form.angle.value, form.staticMu, form.kineticMu].some(
      (text) => text.trim() === ""
    )
  ) {
    return { input: null, unreadable: null, missing: true };
  }
  if (mass === null) {
    return fail("mass");
  }
  if (angle === null) {
    return fail("angle");
  }
  if (staticMu === null) {
    return fail("staticMu");
  }
  if (kineticMu === null) {
    return fail("kineticMu");
  }
  if (applied === null) {
    return fail("applied");
  }
  if (form.length.value.trim() !== "" && length === null) {
    return fail("length");
  }
  if (gravity === null) {
    return fail("gravityCustomLabel");
  }
  return {
    input: {
      mass: toBase(mass, MASS, form.mass.unit),
      angle: toBase(angle, ANGLE, form.angle.unit),
      staticMu,
      kineticMu,
      gravity,
      applied: toBase(applied, FORCE, form.applied.unit),
      length: length === null ? null : toBase(length, LENGTH, form.length.unit),
    },
    unreadable: null,
    missing: false,
  };
}

function forceText(fmt: Fmt, newtons: number, sig: number): string {
  return withUnit(formatSig(fmt, newtons, sig), "N");
}

function verdictOf(result: InclineResult, strings: Strings): string {
  if (result.motion === "down") {
    return strings.verdictDown;
  }
  return result.motion === "up" ? strings.verdictUp : strings.verdictRest;
}

function Stats({
  input,
  result,
  strings,
  fmt,
  sig,
}: {
  input: InclineInput;
  result: InclineResult;
  strings: Strings;
  fmt: Fmt;
  sig: number;
}) {
  const f = (newtons: number) => forceText(fmt, newtons, sig);
  const accelLabel = {
    down: strings.accelerationDown,
    up: strings.accelerationUp,
    rest: strings.accelerationRest,
  }[result.motion];
  const accel = withUnit(
    formatSig(fmt, Math.abs(result.acceleration), sig),
    "m/s²"
  );
  const verdict = verdictOf(result, strings);
  const critical = withUnit(
    formatSig(fmt, result.criticalAngle * (180 / Math.PI), sig),
    "°"
  );
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.verdictLabel} value={verdict} />
        <StatCard emphasis label={accelLabel} value={accel} />
        <StatCard
          emphasis
          label={
            result.motion === "rest"
              ? strings.frictionStatic
              : strings.frictionActing
          }
          value={f(result.friction)}
        />
      </StatGrid>
      <StatGrid>
        <StatCard label={strings.weight} value={f(result.weight)} />
        <StatCard label={strings.parallel} value={f(result.parallel)} />
        <StatCard
          label={strings.perpendicular}
          value={f(result.perpendicular)}
        />
        <StatCard label={strings.normal} value={f(result.normal)} />
        <StatCard label={strings.maxStatic} value={f(result.maxStatic)} />
        <StatCard
          label={strings.kineticFriction}
          value={f(result.kineticFriction)}
        />
      </StatGrid>
      <p className={HINT}>
        {strings.criticalAngle}: {critical}
      </p>
      <h3 className={WIDGET_H3}>{strings.pushHeading}</h3>
      <StatGrid>
        <StatCard label={strings.pushToStart} value={f(result.pushToStart)} />
        <StatCard
          label={strings.pushToKeep}
          value={f(result.pushToKeepMoving)}
        />
        {result.pushToHold > 0 ? (
          <StatCard label={strings.pushToHold} value={f(result.pushToHold)} />
        ) : null}
      </StatGrid>
      {result.rise !== null && input.length !== null && input.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.travelHeading}</h3>
          <StatGrid>
            {result.travel === null ? null : (
              <StatCard
                label={strings.travelTime}
                value={withUnit(formatSig(fmt, result.travel.time, sig), "s")}
              />
            )}
            {result.travel === null ? null : (
              <StatCard
                label={strings.travelSpeed}
                value={withUnit(
                  formatSig(fmt, result.travel.speed, sig),
                  "m/s"
                )}
              />
            )}
            <StatCard
              label={strings.rise}
              value={withUnit(formatSig(fmt, result.rise, sig), "m")}
            />
            {result.mechanicalAdvantage === null ? null : (
              <StatCard
                label={strings.advantage}
                value={formatSig(fmt, result.mechanicalAdvantage, sig)}
              />
            )}
          </StatGrid>
        </>
      ) : null}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={t(strings.copyText, {
          verdict,
          a: accel,
          n: f(result.normal),
          f: f(result.friction),
        })}
      />
    </>
  );
}

export default function InclinedPlaneCalculatorWidget() {
  const strings = useToolUi(INCLINED_PLANE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [sigFigs, setSigFigs] = useState("4");
  const sig = Number(sigFigs);
  const reading = readForm(form);
  const result = reading.input ? solveIncline(reading.input) : null;
  const unitName = (name: string) => t(strings.unitFor, { name });
  const patchField = (
    key: "mass" | "angle" | "applied" | "length",
    change: Partial<Field>
  ) => setForm({ ...form, [key]: { ...form[key], ...change } });

  return (
    <>
      <p className={HINT}>{strings.instructions}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <QuantityField
          id="ip-mass"
          invalid={reading.unreadable === "mass"}
          label={strings.mass}
          onUnit={(unit) => patchField("mass", { unit })}
          onValue={(value) => patchField("mass", { value })}
          unit={form.mass.unit}
          unitLabel={unitName(strings.mass)}
          units={unitOptions(MASS)}
          value={form.mass.value}
        />
        <QuantityField
          hint={strings.angleHint}
          id="ip-angle"
          invalid={reading.unreadable === "angle"}
          label={strings.angle}
          onUnit={(unit) => patchField("angle", { unit })}
          onValue={(value) => patchField("angle", { value })}
          unit={form.angle.unit}
          unitLabel={unitName(strings.angle)}
          units={unitOptions(ANGLE)}
          value={form.angle.value}
        />
        <QuantityField
          id="ip-static"
          invalid={reading.unreadable === "staticMu"}
          label={strings.staticMu}
          onUnit={() => undefined}
          onValue={(staticMu) => setForm({ ...form, staticMu })}
          unit="1"
          unitLabel={unitName(strings.staticMu)}
          units={unitOptions(COEFFICIENT)}
          value={form.staticMu}
        />
        <QuantityField
          hint={strings.muHint}
          id="ip-kinetic"
          invalid={reading.unreadable === "kineticMu"}
          label={strings.kineticMu}
          onUnit={() => undefined}
          onValue={(kineticMu) => setForm({ ...form, kineticMu })}
          unit="1"
          unitLabel={unitName(strings.kineticMu)}
          units={unitOptions(COEFFICIENT)}
          value={form.kineticMu}
        />
        <QuantityField
          hint={strings.appliedHint}
          id="ip-applied"
          invalid={reading.unreadable === "applied"}
          label={strings.applied}
          onUnit={(unit) => patchField("applied", { unit })}
          onValue={(value) => patchField("applied", { value })}
          unit={form.applied.unit}
          unitLabel={unitName(strings.applied)}
          units={unitOptions(FORCE)}
          value={form.applied.value}
        />
        <QuantityField
          hint={strings.lengthHint}
          id="ip-length"
          invalid={reading.unreadable === "length"}
          label={strings.length}
          onUnit={(unit) => patchField("length", { unit })}
          onValue={(value) => patchField("length", { value })}
          unit={form.length.unit}
          unitLabel={unitName(strings.length)}
          units={unitOptions(LENGTH)}
          value={form.length.value}
        />
        <GravityPicker
          idPrefix="ip"
          invalid={reading.unreadable === "gravityCustomLabel"}
          labels={{
            label: strings.gravity,
            earth: strings.gravityEarth,
            moon: strings.gravityMoon,
            mars: strings.gravityMars,
            custom: strings.gravityCustom,
            customLabel: strings.gravityCustomLabel,
            unitFor: strings.unitFor,
          }}
          onChange={(gravity) => setForm({ ...form, gravity })}
          state={form.gravity}
        />
        <SelectField
          id="ip-sig"
          label={strings.rounding}
          onChange={setSigFigs}
          options={SIG_FIG_CHOICES.map((choice) => ({
            value: choice,
            label: tPlural(strings.sigFigs, locale, Number(choice), {
              count: fmt(Number(choice)),
            }),
          }))}
          value={sigFigs}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {reading.unreadable === null ? null : (
          <ErrorNotice>
            {t(strings.unreadable, { name: strings[reading.unreadable] })}
          </ErrorNotice>
        )}
        {reading.missing ? <Notice>{strings.enterPrompt}</Notice> : null}
        {result !== null && "error" in result ? (
          <ErrorNotice>{strings.errors[result.error]}</ErrorNotice>
        ) : null}
        {reading.input !== null && result !== null && !("error" in result) ? (
          <>
            <Stats
              fmt={fmt}
              input={reading.input}
              result={result}
              sig={sig}
              strings={strings}
            />
            <Working
              heading={strings.workingHeading}
              steps={inclineSteps(reading.input, result, strings, fmt, sig)}
            />
            <h3 className={WIDGET_H3}>{strings.diagramHeading}</h3>
            <InclineDiagram
              angle={reading.input.angle}
              forces={{
                weight: result.weight,
                normal: result.normal,
                friction: result.friction,
                frictionDirection: result.frictionDirection,
                applied: reading.input.applied,
              }}
              labels={{
                title: strings.diagramTitle,
                weight: strings.diagramWeight,
                normal: strings.diagramNormal,
                friction: strings.diagramFriction,
                applied: strings.diagramApplied,
                angle:
                  formatSig(
                    fmt,
                    fromBase(reading.input.angle, ANGLE, form.angle.unit),
                    sig
                  ) + unitSymbol(ANGLE, form.angle.unit),
              }}
            />
            <p className={HINT}>{strings.diagramNote}</p>
            <p className={HINT}>{strings.assumptions}</p>
          </>
        ) : null}
      </section>
    </>
  );
}
